import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
// Importing components
import { ProgressBarPreview } from './progress-bar-preview';
// Importing hooks
import { useSliderPosition } from '../../../hooks/use-slider-position';
import { useShallow } from 'zustand/react/shallow';
import { useTheme } from '@mui/material/styles';
// Importing store
import { useMediaStore } from '../../../store/media.store';
import { usePlayerStore } from '../../../store/player.store';
// Importing 3rd party components
import Box from '@mui/material/Box';
import Slider from '@mui/material/Slider';
// Importing 3rd party dependencies
import { useRef } from 'react';
// ----------------------------------------------------------------------
export const ProgressBar = () => {
    // Adding hooks
    const theme = useTheme();
    const ref = useRef(null);
    const { useDimensions } = useSliderPosition();
    const { width } = useDimensions(ref);
    // Adding media data
    const { media } = useMediaStore(useShallow((state) => ({
        media: state.media,
    })));
    // Adding player data
    const { duration, loaded, played, player, setPlayed, setSliderHover, setSliderHoverPosition } = usePlayerStore(useShallow((state) => ({
        duration: state.duration,
        loaded: state.loaded,
        played: state.played,
        player: state.player,
        setPlayed: state.setPlayed,
        setSliderHover: state.setSliderHover,
        setSliderHoverPosition: state.setSliderHoverPosition,
    })));
    // Adding callback to handle scrubbing
    const handleScrubbing = (event, value) => {
        var _a;
        setPlayed(value);
        (_a = player.current) === null || _a === void 0 ? void 0 : _a.seekTo(value, 'fraction');
    };
    // Adding callback to handle mouse interaction with the slider
    const handleMouseInteraction = (event) => {
        const rect = event.currentTarget.getBoundingClientRect();
        setSliderHoverPosition((event.clientX - rect.left) / width);
    };
    // Adding marks for chapters
    const marks = media === null || media === void 0 ? void 0 : media.chapters.map((chapter) => ({
        value: chapter.to / 1000 / duration,
    }));
    return (_jsx(ProgressBarPreview, { children: _jsxs(Box, { sx: { position: 'relative' }, children: [_jsx(Slider, { ref: ref, defaultValue: 0, "aria-label": "Slider", marks: marks, max: 1, min: 0, step: 0.01, value: played, onChange: handleScrubbing, onMouseEnter: () => setSliderHover(true), onMouseLeave: () => setSliderHover(false), onMouseMove: handleMouseInteraction, sx: {
                        color: theme.palette.primary.main,
                        height: 6,
                        pb: 0,
                        position: 'absolute',
                        zIndex: 99,
                        '& .MuiSlider-mark': {
                            backgroundColor: theme.palette.primary.main,
                            borderRadius: 4,
                            height: 14,
                            width: 4,
                        },
                        '& .MuiSlider-thumb': {
                            width: 18,
                            height: 18,
                            transition: '0.3s cubic-bezier(0.47, 1.64, 0.41, 0.8)',
                            '&::before': {
                                boxShadow: '0 2px 12px 0 rgba(0, 0, 0, 0.4)',
                            },
                            '&:hover, &.Mui-focusVisible': {
                                boxShadow: '0px 0px 0px 8px rgb(255 255 255 / 16%)',
                            },
                            '&.Mui-active': {
                                width: 24,
                                height: 24,
                            },
                        },
                        '& .MuiSlider-rail': {
                            backgroundColor: 'white',
                            opacity: 0.3,
                        },
                    } }), _jsx(Slider, { "aria-hidden": true, defaultValue: 0, max: 1, min: 0, step: 0.01, value: loaded, sx: {
                        color: '#ffffff',
                        display: 'flex',
                        opacity: 0.5,
                        pb: 0,
                        top: 1,
                        '& .MuiSlider-rail': {
                            display: 'none',
                        },
                        '& .MuiSlider-thumb': {
                            display: 'none',
                        },
                    } })] }) }));
};
